import React, {useState, useEffect} from 'react';
import {clasesAPI, profesoresAPI} from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';
import ErrorMessage from '../Common/ErrorMessage';
import ClassDetailModal from '../Common/ClassDetailModal';
import {getDayName, formatTime, compareEs} from '../../services/utils';
import {CalendarIcon, ClockIcon, UserIcon, MusicalNoteIcon} from '@heroicons/react/24/outline';

const Home = ({currentYear}) => {
    const [selectedDay, setSelectedDay] = useState('1'); // Lunes por defecto
    const [selectedProfessor, setSelectedProfessor] = useState(null); // Para Horario del Profesor
    const [classes, setClasses] = useState([]);
    const [groupedClasses, setGroupedClasses] = useState({});
    const [allProfessorClasses, setAllProfessorClasses] = useState({}); // Clases agrupadas por profesor (sin filtro de día)
    const [professors, setProfessors] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedClass, setSelectedClass] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [timeSlots, setTimeSlots] = useState([]);

    const days = [
        { id: '1', name: 'Lunes'},
        { id: '2', name: 'Martes'},
        { id: '3', name: 'Miércoles'},
        { id: '4', name: 'Jueves'},
        { id: '5', name: 'Viernes'}
    ];

    // Generar franjas horarias de 11:00 a 22:00
    const generateTimeSlots = () => {
        const slots = [];
        for (let hour = 11; hour < 22; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const time = `${hour
                    .toString()
                    .padStart(2, '0')}:${minute
                    .toString()
                    .padStart(2, '0')}`;
                slots.push(time);
            }
        }
        return slots;
    };

    useEffect(() => {
        setTimeSlots(generateTimeSlots());
        fetchData();
    }, [selectedDay, currentYear]);

    const fetchData = async () => {
        try {
            setLoading(true);

            const [classesRes, professorsRes] = await Promise.all(
                [clasesAPI.getByYear(currentYear), profesoresAPI.getAll()]
            );

            // Filtrar clases por día seleccionado y ordenar por hora de inicio
            const filteredClasses = classesRes
                .data
                .filter(clase => clase.DiaSemana === selectedDay)
                .sort((a, b) => a.H_Inicio.localeCompare(b.H_Inicio));

            setClasses(filteredClasses);

            // Crear mapa de profesores
            const professorsMap = {};
            professorsRes
                .data
                .forEach(prof => {
                    professorsMap[prof.IDProfesor] = prof;
                });
            setProfessors(professorsMap);

            // Agrupar clases por profesor (filtradas por día)
            const grouped = filteredClasses.reduce((acc, clase) => {
                if (!acc[clase.ProfesorID]) {
                    acc[clase.ProfesorID] = [];
                }
                acc[clase.ProfesorID].push(clase);
                return acc;
            }, {});

            setGroupedClasses(grouped);

            // Agrupar todas las clases por profesor (sin filtro de día) para el "Horario
            // del Profesor"
            const allGroupedByProf = classesRes
                .data
                .reduce((acc, clase) => {
                    if (!acc[clase.ProfesorID]) {
                        acc[clase.ProfesorID] = [];
                    }
                    acc[clase.ProfesorID].push(clase);
                    return acc;
                }, {});

            setAllProfessorClasses(allGroupedByProf);

            setError(null);
        } catch (err) {
            setError('Error al cargar las clases');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Establecer profesor por defecto solo la primera vez
    useEffect(() => {
        if (selectedProfessor === null && Object.keys(allProfessorClasses).length > 0) {
            const firstProf = Object
                .keys(allProfessorClasses)
                .sort((a, b) => {
                    const profA = professors[a]?.Apellido || '';
                    const profB = professors[b]?.Apellido || '';
                    return compareEs(profA, profB);
                })[0];
            setSelectedProfessor(firstProf);
        }
    }, [allProfessorClasses, professors]);

    const handleClassClick = (clase) => {
        setSelectedClass(clase);
        setIsModalOpen(true);
    };

    // Calcular posición y ancho de un cuadro de clase basado en la hora
    const calculateClassPosition = (startTime, endTime) => {
        const [startHour, startMinute] = startTime
            .split(':')
            .map(Number);
        const [endHour, endMinute] = endTime
            .split(':')
            .map(Number);

        // Calcular minutos desde las 11:00 (nuestro inicio) Asegurarnos de que no sea
        // negativo
        const startMinutes = Math.max((startHour - 11) * 60 + startMinute, 0);
        const endMinutes = (endHour - 11) * 60 + endMinute;
        const duration = Math.min(endMinutes - startMinutes, 4 * 60); // Máximo 4 horas

        // Posición como porcentaje (cada 30 minutos = 100% / 22 slots)
        const position = (startMinutes / 30) * (100 / 22);
        const width = (duration / 30) * (100 / 22);

        return {
            position: Math.min(position, 100), // Asegurar que no sobrepase el 100%
            width: Math.min(width, 100 - position) // Asegurar que quepa en el contenedor
        };
    };

    // Función para obtener color único basado en asignatura
    const getClassColor = (asignaturaId) => {
        const colors = [
            'bg-blue-100 border-blue-300 hover:bg-blue-200',
            'bg-green-100 border-green-300 hover:bg-green-200',
            'bg-yellow-100 border-yellow-300 hover:bg-yellow-200',
            'bg-purple-100 border-purple-300 hover:bg-purple-200',
            'bg-pink-100 border-pink-300 hover:bg-pink-200',
            'bg-indigo-100 border-indigo-300 hover:bg-indigo-200',
            'bg-red-100 border-red-300 hover:bg-red-200',
            'bg-teal-100 border-teal-300 hover:bg-teal-200',
            'bg-orange-100 border-orange-300 hover:bg-orange-200',
            'bg-cyan-100 border-cyan-300 hover:bg-cyan-200'
        ];

        // Usar 0 como default si asignaturaId es undefined
        const index = asignaturaId
            ? (asignaturaId % colors.length)
            : 0;
        return colors[index];
    };

    // Función para obtener solo el color base (sin hover)
    const getBaseColor = (asignaturaId) => {
        const colorClass = getClassColor(asignaturaId);
        if (!colorClass) 
            return 'bg-blue-100';
        
        // Encontrar la clase que empieza con 'bg-'
        const bgClass = colorClass
            .split(' ')
            .find(cls => cls.startsWith('bg-'));
        return bgClass || 'bg-blue-100';
    };

    // Calcular horas totales de clases de un profesor
    const calculateTotalHours = (classes) => {
        let totalMinutes = 0;
        classes.forEach(clase => {
            const [startHour, startMinute] = clase
                .H_Inicio
                .split(':')
                .map(Number);
            const [endHour, endMinute] = clase
                .H_Final
                .split(':')
                .map(Number);

            const startTotalMinutes = startHour * 60 + startMinute;
            const endTotalMinutes = endHour * 60 + endMinute;

            totalMinutes += (endTotalMinutes - startTotalMinutes);
        });

        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}h ${minutes}m`;
    };

    // Obtener profesores ordenados por Apellido
    const sortedProfessors = Object
        .keys(groupedClasses)
        .sort((a, b) => {
            const profA = professors[a]?.Apellido || '';
            const profB = professors[b]?.Apellido || '';
            return compareEs(profA, profB);
        });

    if (loading) 
        return <LoadingSpinner text="Cargando horarios..."/>;
    if (error) 
        return <ErrorMessage message={error} onRetry={fetchData}/>;
    
    return (
        <div className="space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Horario del Día</h2>
                        <p className="text-gray-600">Visualización por profesor y horario</p>
                    </div>
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                            <CalendarIcon className="h-5 w-5 text-gray-400"/>
                            <div className="flex space-x-2 bg-gray-100 p-1 rounded-lg">
                                {
                                    days.map(day => {
                                        const dayInitials = ['L', 'M', 'X', 'J', 'V'];
                                        const dayIndex = parseInt(day.id) - 1;
                                        const initial = dayInitials[dayIndex];
                                        const isSelected = selectedDay === day.id;
                                        
                                        return (
                                            <button
                                                key={day.id}
                                                onClick={() => setSelectedDay(day.id)}
                                                title={day.name}
                                                className={`w-10 h-10 rounded-md font-semibold text-sm transition-colors ${isSelected ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-200'}`}>
                                                {initial}
                                            </button>
                                        );
                                    })
                                }
                            </div>
                        </div>
                    </div>
                </div>

                {
                    sortedProfessors.length === 0
                        ? (
                            <div className="text-center py-12">
                                <CalendarIcon className="h-16 w-16 text-gray-300 mx-auto mb-4"/>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No hay clases programadas</h3>
                                <p className="text-gray-500">No hay clases programadas para {getDayName(selectedDay)}</p>
                            </div>
                        )
                        : (
                            <div className="overflow-x-auto">
                                <div className="min-w-full">
                                    {/* Cabecera con horas */}
                                    <div className="flex border-b border-gray-200">
                                        <div
                                            className="w-48 flex-shrink-0 border-r border-gray-200 py-3 px-4 bg-gray-50">
                                            <span className="text-sm font-medium text-gray-500">Profesor</span>
                                        </div>
                                        <div className="flex-1 relative">
                                            <div className="flex">
                                                {
                                                    timeSlots.map((time, index) => (
                                                        <div
                                                            key={time}
                                                            className="flex-1 text-center py-3 border-r border-gray-200"
                                                            style={{minWidth: '40px'}}>
                                                            {index % 2 === 0 && (<span className="text-xs text-gray-500 font-medium">{time.substring(0, 5)}</span>)}
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </div>
                                    </div>

                                    {/* Filas por profesor */}
                                    {
                                        sortedProfessors.map((profesorId) => {
                                            const profesor = professors[profesorId];
                                            const profesorClasses = groupedClasses[profesorId] || [];

                                            return (
                                                <div
                                                    key={profesorId}
                                                    className="flex border-b border-gray-200 hover:bg-gray-50">
                                                    {/* Columna del profesor */}
                                                    <div className="w-48 flex-shrink-0 border-r border-gray-200 py-4 px-4">
                                                        <div className="flex items-center">
                                                            <div>
                                                                <div className="text-sm font-medium text-gray-900">
                                                                    {profesor?.Nombre} { profesor?.Apellido}
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    {profesorClasses.length} clase{profesorClasses.length !== 1? 's' : ''} • {calculateTotalHours(profesorClasses)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Columna de horarios */}
                                                    <div className="flex-1 relative min-h-[80px] py-4">
                                                        <div className="absolute inset-0 flex">
                                                            {
                                                                timeSlots.map((time, index) => (
                                                                    <div
                                                                        key={time}
                                                                        className="flex-1 border-r border-gray-100"
                                                                        style={{minWidth: '40px'}}/>
                                                                ))
                                                            }
                                                        </div>

                                                        {/* Cuadros de clases */}
                                                        {
                                                            profesorClasses.map((clase) => {
                                                                const {position, width} = calculateClassPosition(clase.H_Inicio, clase.H_Final);
                                                                const colorClass = getClassColor(clase.AsignaturaID);

                                                                return (
                                                                    <div
                                                                        key={clase.IDClase}
                                                                        className={`absolute top-2 bottom-2 rounded-lg border ${colorClass} shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md`}
                                                                        style={{
                                                                            left: `${position}%`,
                                                                            width: `${width}%`,
                                                                            minWidth: '60px',
                                                                            // Asegurar que no se salga del contenedor
                                                                            maxWidth: `calc(100% - ${position}%)`
                                                                        }}
                                                                        onClick={() => handleClassClick(clase)}
                                                                        title={`${clase.Asignatura} - ${formatTime(clase.H_Inicio)} a ${formatTime(clase.H_Final)}`}>
                                                                        <div className="p-2 h-full overflow-hidden">
                                                                            <div className="flex flex-col h-full">
                                                                                <div className="flex-1">
                                                                                    <div className="flex items-start justify-between">
                                                                                        <div className="flex-1">
                                                                                            <h4 className="text-xs font-semibold text-gray-900 truncate">
                                                                                                {clase.Asignatura}
                                                                                            </h4>
                                                                                            {
                                                                                                clase.Instrumento && (
                                                                                                    <div className="flex items-center mt-1">
                                                                                                        <MusicalNoteIcon className="h-3 w-3 text-gray-500 mr-1"/>
                                                                                                        <span className="text-xs text-gray-600 truncate">
                                                                                                            {clase.Instrumento}
                                                                                                        </span>
                                                                                                    </div>
                                                                                                )
                                                                                            }
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                                <div className="mt-1">
                                                                                    <div className="flex items-center text-xs text-gray-500">
                                                                                        <ClockIcon className="h-3 w-3 mr-1"/>
                                                                                        <span>
                                                                                            {formatTime(clase.H_Inicio)} - {formatTime(clase.H_Final)}
                                                                                        </span>
                                                                                    </div>
                                                                                    {
                                                                                        clase.AlumnosCount > 0 && (
                                                                                            <div className="text-xs text-gray-500 mt-1">
                                                                                                {clase.AlumnosCount}
                                                                                                alumno{clase.AlumnosCount !== 1 ? 's' : ''}
                                                                                            </div>
                                                                                        )
                                                                                    }
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })
                                                        }
                                                    </div>
                                                </div>
                                            );
                                        })
                                    }
                                </div>
                            </div>
                        )
                }

                {/* Leyenda de colores */}
                {
                    classes.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <h4 className="text-sm font-medium text-gray-900 mb-3">Leyenda de Asignaturas</h4>
                            <div className="flex flex-wrap gap-2">
                                {
                                    Array
                                        .from(new Set(classes.map(c => c.AsignaturaID)))
                                        .slice(0, 6)
                                        .map((asignaturaId, index) => {
                                            const clase = classes.find(c => c.AsignaturaID === asignaturaId);
                                            const baseColor = getBaseColor(asignaturaId);

                                            return (
                                                <div key={asignaturaId} className="flex items-center">
                                                    <div className={`w-3 h-3 rounded ${baseColor} mr-2`}/>
                                                    <span className="text-xs text-gray-600">
                                                        {clase ?.Asignatura || `Asignatura ${asignaturaId}`}
                                                    </span>
                                                    {
                                                        index < 5 && index < Array
                                                            .from(new Set(classes.map(c => c.AsignaturaID)))
                                                            .slice(0, 6)
                                                            .length - 1 && (<span className="mx-2 text-gray-300">•</span>)
                                                    }
                                                </div>
                                            );
                                        })
                                }
                            </div>
                        </div>
                    )
                }
            </div>

            {/* Horario del Profesor */}
            <div className="bg-white shadow rounded-lg p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Horario del Profesor</h2>
                        <p className="text-gray-600">Visualización por día y horario</p>
                    </div>
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                            <UserIcon className="h-5 w-5 text-gray-400"/>
                            <select
                                value={selectedProfessor || ''}
                                onChange={(e) => setSelectedProfessor(e.target.value)}
                                className="block w-48 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md">
                                <option value="">Seleccionar profesor...</option>
                                {
                                    Object
                                        .keys(allProfessorClasses)
                                        .sort((a, b) => {
                                            const profA = professors[a]?.Apellido || '';
                                            const profB = professors[b]?.Apellido || '';
                                            return compareEs(profA, profB);
                                        })
                                        .map(profesorId => {
                                            const profesor = professors[profesorId];
                                            return (
                                                <option key={profesorId} value={profesorId}>
                                                    { profesor?.Nombre } { profesor?.Apellido}
                                                </option>
                                            );
                                        })
                                }
                            </select>
                        </div>
                    </div>
                </div>

                {
                    !selectedProfessor || !allProfessorClasses[selectedProfessor] || allProfessorClasses[selectedProfessor].length === 0
                        ? (
                            <div className="text-center py-12">
                                <UserIcon className="h-16 w-16 text-gray-300 mx-auto mb-4"/>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No hay clases programadas</h3>
                                <p className="text-gray-500">Selecciona un profesor para ver su horario</p>
                            </div>
                        )
                        : (
                            <div className="overflow-x-auto">
                                <div className="min-w-full">
                                    {/* Agrupar clases por día */}
                                    {
                                        selectedProfessor && (() => {
                                            const profesorClasses = allProfessorClasses[selectedProfessor] || [];
                                            const classesByDay = {};

                                            // Agrupar clases por día
                                            profesorClasses.forEach(clase => {
                                                if (!classesByDay[clase.DiaSemana]) {
                                                    classesByDay[clase.DiaSemana] = [];
                                                }
                                                classesByDay[clase.DiaSemana].push(clase);
                                            });

                                            // Ordenar por día y luego por hora
                                            const sortedDays = Object
                                                .keys(classesByDay)
                                                .sort((a, b) => parseInt(a) - parseInt(b));

                                            return (
                                                <>
                                                    {/* Cabecera única con horas */}
                                                    <div className="flex border-b border-gray-200 sticky top-0 z-10 bg-white">
                                                        <div className="w-32 flex-shrink-0 border-r border-gray-200 py-3 px-4 bg-gray-50">
                                                            <span className="text-xs font-medium text-gray-900">Día</span>
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex">
                                                                {
                                                                    timeSlots.map((time, index) => (
                                                                        <div
                                                                            key={time}
                                                                            className="flex-1 text-center py-3 border-r border-gray-200"
                                                                            style={{minWidth: '40px'}}>
                                                                            {
                                                                                (index % 2 === 0 || index === timeSlots.length - 1) && (
                                                                                    <span className="text-xs text-gray-500 font-medium">
                                                                                        {index === timeSlots.length - 1 ? '22h' : `${time.substring(0, 2)}h`}
                                                                                    </span>
                                                                                )
                                                                            }
                                                                        </div>
                                                                    ))
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {sortedDays.map(dayId => {
                                                        const dayClasses = classesByDay[dayId].sort(
                                                            (a, b) => a.H_Inicio.localeCompare(b.H_Inicio)
                                                        );
                                                        const dayName = getDayName(dayId);

                                                        return (
                                                            <div key={dayId} className="flex border-b border-gray-200 last:border-b-0">
                                                                <div className="w-32 flex-shrink-0 border-r border-gray-200 py-4 px-4 bg-gray-50">
                                                                    <span className="text-xs font-medium text-gray-900">{dayName}</span>
                                                                </div>

                                                                <div className="flex-1 relative min-h-[80px] py-4">
                                                                    <div className="absolute inset-0 flex">
                                                                        {
                                                                            timeSlots.map((time) => (
                                                                                <div
                                                                                    key={time}
                                                                                    className="flex-1 border-r border-gray-100"
                                                                                    style={{minWidth: '40px'}}/>
                                                                            ))
                                                                        }
                                                                    </div>

                                                                    {/* Cuadros de clases */}
                                                                    {
                                                                        dayClasses.map((clase) => {
                                                                            const {position, width} = calculateClassPosition(clase.H_Inicio, clase.H_Final);
                                                                            const colorClass = getClassColor(clase.AsignaturaID);

                                                                            return (
                                                                                <div
                                                                                    key={clase.IDClase}
                                                                                    className={`absolute top-2 bottom-2 rounded-lg border ${colorClass} shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md`}
                                                                                    style={{
                                                                                        left: `${position}%`,
                                                                                        width: `${width}%`,
                                                                                        minWidth: '60px',
                                                                                        maxWidth: `calc(100% - ${position}%)`
                                                                                    }}
                                                                                    onClick={() => handleClassClick(clase)}
                                                                                    title={`${clase.Asignatura} - ${formatTime(clase.H_Inicio)} a ${formatTime(clase.H_Final)}`}>
                                                                                    <div className="p-2 h-full overflow-hidden">
                                                                                        <div className="flex flex-col h-full">
                                                                                            <div className="flex-1">
                                                                                                <div className="flex items-start justify-between">
                                                                                                    <div className="flex-1">
                                                                                                        <h4 className="text-xs font-semibold text-gray-900 truncate">
                                                                                                            {clase.Asignatura}
                                                                                                        </h4>
                                                                                                        {
                                                                                                            clase.Instrumento && (
                                                                                                                <div className="flex items-center mt-1">
                                                                                                                    <MusicalNoteIcon className="h-3 w-3 text-gray-500 mr-1"/>
                                                                                                                    <span className="text-xs text-gray-600 truncate">
                                                                                                                        {clase.Instrumento}
                                                                                                                    </span>
                                                                                                                </div>
                                                                                                            )
                                                                                                        }
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                            <div className="mt-1">
                                                                                                <div className="flex items-center text-xs text-gray-500">
                                                                                                    <ClockIcon className="h-3 w-3 mr-1"/>
                                                                                                    <span>
                                                                                                        {formatTime(clase.H_Inicio)} - {formatTime(clase.H_Final)}
                                                                                                    </span>
                                                                                                </div>
                                                                                                {
                                                                                                    clase.AlumnosCount > 0 && (
                                                                                                        <div className="text-xs text-gray-500 mt-1">
                                                                                                            {clase.AlumnosCount}
                                                                                                            alumno{clase.AlumnosCount !== 1 ? 's' : ''}
                                                                                                        </div>
                                                                                                    )
                                                                                                }
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })
                                                                    }
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            );
                                        })()
                                    }
                                </div>
                            </div>
                        )
                }

                {/* Leyenda de colores - Horario del Profesor */}
                {
                    selectedProfessor && allProfessorClasses[selectedProfessor] && allProfessorClasses[selectedProfessor].length > 0 && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <h4 className="text-sm font-medium text-gray-900 mb-3">Leyenda de Asignaturas</h4>
                            <div className="flex flex-wrap gap-2">
                                {
                                    Array
                                        .from(
                                            new Set(allProfessorClasses[selectedProfessor].map(c => c.AsignaturaID))
                                        )
                                        .slice(0, 6)
                                        .map((asignaturaId, index) => {
                                            const clase = allProfessorClasses[selectedProfessor].find(
                                                c => c.AsignaturaID === asignaturaId
                                            );
                                            const baseColor = getBaseColor(asignaturaId);

                                            return (
                                                <div key={asignaturaId} className="flex items-center">
                                                    <div className={`w-3 h-3 rounded ${baseColor} mr-2`}/>
                                                    <span className="text-xs text-gray-600">
                                                        { clase?.Asignatura || `Asignatura ${asignaturaId}` }
                                                    </span>
                                                    {
                                                        index < 5 && index < Array
                                                            .from(new Set(allProfessorClasses[selectedProfessor].map(c => c.AsignaturaID)))
                                                            .slice(0, 6)
                                                            .length - 1 && (<span className="mx-2 text-gray-300">•</span>)
                                                    }
                                                </div>
                                            );
                                        })
                                }
                            </div>
                        </div>
                    )
                }
            </div>
            {
                selectedClass && (
                    <ClassDetailModal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        classData={selectedClass}/>
                )
            }
        </div>
    );
};

export default Home;
